/**
 * Workforce Mutation Hooks with Cache Invalidation
 *
 * These hooks use useMutation from React Query to handle create, update, and delete
 * operations with automatic cache invalidation.
 *
 * Features:
 * - Automatic cache invalidation after successful mutations
 * - Error handling with rollback support
 * - Loading and error states
 * - Toast notifications integration ready
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from './useTenant'
import { toast } from 'sonner'
import {
  createEmployee,
  updateEmployee,
  deleteEmployee,
  createDepartment,
  updateDepartment,
  createRole,
  updateRole,
  bulkUpdateEmployeeStatus,
  bulkTransferDepartment,
  bulkReassignManager,
  batchProcessPayroll,
  getEmployeePictureUploadUrl,
  uploadEmployeePictureToS3,
  confirmEmployeePictureUpload
} from '@/api/workforce'
import type {
  Employee,
  Department,
  Role,
  CreateEmployeeData,
  CreateDepartmentData,
  CreateRoleData,
  CreatePayrollCostData
} from '@/types/workforce'
import { invalidateWorkforceQueries, queryKeys } from '@/constants/queryKeys'

// ============================================================================
// EMPLOYEE MUTATIONS
// ============================================================================

/**
 * useCreateEmployee - Mutation hook for creating employees
 *
 * Automatically invalidates:
 * - All employee list queries
 * - Employee stats
 * - Dashboard overview
 * - Organizational hierarchy
 *
 * @returns Mutation object with mutate, mutateAsync, isLoading, error
 */
export function useCreateEmployee() {
  const queryClient = useQueryClient()
  const { tenantDomain } = useTenant()

  return useMutation({
    mutationFn: async (employeeData: CreateEmployeeData) => {
      return await createEmployee(tenantDomain, employeeData)
    },
    onSuccess: () => {
      toast.success('Colaborador criado com sucesso!')

      // Invalidate all employee-related queries
      queryClient.invalidateQueries(invalidateWorkforceQueries.employees(tenantDomain))
      queryClient.invalidateQueries({ queryKey: ['workforce', 'employees', tenantDomain, 'stats'] })
      queryClient.invalidateQueries({ queryKey: ['workforce', 'dashboard', tenantDomain] })
      queryClient.invalidateQueries({ queryKey: ['workforce', 'hierarchy', tenantDomain] })
    },
    onError: (err) => {
      console.error('[CreateEmployee] Error:', err)
      toast.error(err instanceof Error ? err.message : 'Erro ao criar colaborador')
    }
  })
}

/**
 * useUpdateEmployee - Mutation hook for updating employees
 *
 * Automatically invalidates:
 * - All employee list queries
 * - Specific employee detail query
 * - Employee stats
 * - Dashboard overview
 * - Organizational hierarchy (if manager changes)
 *
 * @returns Mutation object with mutate, mutateAsync, isLoading, error
 */
export function useUpdateEmployee() {
  const queryClient = useQueryClient()
  const { tenantDomain } = useTenant()

  return useMutation({
    mutationFn: async ({ employeeId, updates }: { employeeId: string; updates: Partial<Employee> }) => {
      return await updateEmployee(tenantDomain, employeeId, updates)
    },
    onSuccess: (_data, variables) => {
      toast.success('Colaborador atualizado com sucesso!')

      // Invalidate employee list queries
      queryClient.invalidateQueries(invalidateWorkforceQueries.employees(tenantDomain))

      // Invalidate specific employee detail
      queryClient.invalidateQueries(invalidateWorkforceQueries.employee(tenantDomain, variables.employeeId))

      // Invalidate stats and dashboard
      queryClient.invalidateQueries({ queryKey: ['workforce', 'employees', tenantDomain, 'stats'] })
      queryClient.invalidateQueries({ queryKey: ['workforce', 'dashboard', tenantDomain] })

      // If manager changed, invalidate hierarchy
      if (variables.updates.employment?.managerId !== undefined) {
        queryClient.invalidateQueries(invalidateWorkforceQueries.hierarchy(tenantDomain))
      }
    },
    onError: (err) => {
      console.error('[UpdateEmployee] Error:', err)
      toast.error(err instanceof Error ? err.message : 'Erro ao atualizar colaborador')
    }
  })
}

/**
 * useDeleteEmployee - Mutation hook for deleting employees
 *
 * Automatically invalidates:
 * - All employee list queries
 * - Employee stats
 * - Dashboard overview
 * - Organizational hierarchy
 *
 * @returns Mutation object with mutate, mutateAsync, isLoading, error
 */
export function useDeleteEmployee() {
  const queryClient = useQueryClient()
  const { tenantDomain } = useTenant()

  return useMutation({
    mutationFn: async (employeeId: string) => {
      await deleteEmployee(tenantDomain, employeeId)
    },
    onSuccess: () => {
      toast.success('Colaborador removido com sucesso!')

      // Invalidate all employee-related queries
      queryClient.invalidateQueries(invalidateWorkforceQueries.employees(tenantDomain))
      queryClient.invalidateQueries({ queryKey: ['workforce', 'employees', tenantDomain, 'stats'] })
      queryClient.invalidateQueries({ queryKey: ['workforce', 'dashboard', tenantDomain] })
      queryClient.invalidateQueries({ queryKey: ['workforce', 'hierarchy', tenantDomain] })
    },
    onError: (err) => {
      console.error('[DeleteEmployee] Error:', err)
      toast.error(err instanceof Error ? err.message : 'Erro ao remover colaborador')
    }
  })
}

// ============================================================================
// DEPARTMENT MUTATIONS
// ============================================================================

/**
 * useCreateDepartment - Mutation hook for creating departments
 *
 * Automatically invalidates:
 * - All department list queries
 * - Dashboard overview
 *
 * @returns Mutation object with mutate, mutateAsync, isLoading, error
 */
export function useCreateDepartment() {
  const queryClient = useQueryClient()
  const { tenantDomain } = useTenant()

  return useMutation({
    mutationFn: async (departmentData: CreateDepartmentData) => {
      return await createDepartment(tenantDomain, departmentData)
    },
    onSuccess: () => {
      toast.success('Departamento criado com sucesso!')
      queryClient.invalidateQueries(invalidateWorkforceQueries.departments(tenantDomain))
      queryClient.invalidateQueries({ queryKey: ['workforce', 'dashboard', tenantDomain] })
    },
    onError: (err) => {
      console.error('[CreateDepartment] Error:', err)
      toast.error(err instanceof Error ? err.message : 'Erro ao criar departamento')
    }
  })
}

/**
 * useUpdateDepartment - Mutation hook for updating departments
 *
 * Automatically invalidates:
 * - All department list queries
 * - Specific department detail query
 * - Dashboard overview
 *
 * @returns Mutation object with mutate, mutateAsync, isLoading, error
 */
export function useUpdateDepartment() {
  const queryClient = useQueryClient()
  const { tenantDomain } = useTenant()

  return useMutation({
    mutationFn: async ({ departmentId, updates }: { departmentId: string; updates: Partial<Department> }) => {
      return await updateDepartment(tenantDomain, departmentId, updates)
    },
    onSuccess: (_data, variables) => {
      toast.success('Departamento atualizado com sucesso!')
      queryClient.invalidateQueries(invalidateWorkforceQueries.departments(tenantDomain))
      queryClient.invalidateQueries({
        queryKey: ['workforce', 'departments', tenantDomain, variables.departmentId]
      })
      queryClient.invalidateQueries({ queryKey: ['workforce', 'dashboard', tenantDomain] })
    },
    onError: (err) => {
      console.error('[UpdateDepartment] Error:', err)
      toast.error(err instanceof Error ? err.message : 'Erro ao atualizar departamento')
    }
  })
}

// ============================================================================
// ROLE MUTATIONS
// ============================================================================

/**
 * useCreateRole - Mutation hook for creating roles
 *
 * Automatically invalidates:
 * - All role list queries
 * - Dashboard overview
 *
 * @returns Mutation object with mutate, mutateAsync, isLoading, error
 */
export function useCreateRole() {
  const queryClient = useQueryClient()
  const { tenantDomain } = useTenant()

  return useMutation({
    mutationFn: async (roleData: CreateRoleData) => {
      return await createRole(tenantDomain, roleData)
    },
    onSuccess: () => {
      toast.success('Cargo criado com sucesso!')
      queryClient.invalidateQueries(invalidateWorkforceQueries.roles(tenantDomain))
      queryClient.invalidateQueries({ queryKey: ['workforce', 'dashboard', tenantDomain] })
    },
    onError: (err) => {
      console.error('[CreateRole] Error:', err)
      toast.error(err instanceof Error ? err.message : 'Erro ao criar cargo')
    }
  })
}

/**
 * useUpdateRole - Mutation hook for updating roles
 *
 * Automatically invalidates:
 * - All role list queries
 * - Specific role detail query
 * - Dashboard overview
 *
 * @returns Mutation object with mutate, mutateAsync, isLoading, error
 */
export function useUpdateRole() {
  const queryClient = useQueryClient()
  const { tenantDomain } = useTenant()

  return useMutation({
    mutationFn: async ({ roleId, updates }: { roleId: string; updates: Partial<Role> }) => {
      return await updateRole(tenantDomain, roleId, updates)
    },
    onSuccess: (_data, variables) => {
      toast.success('Cargo atualizado com sucesso!')
      queryClient.invalidateQueries(invalidateWorkforceQueries.roles(tenantDomain))
      queryClient.invalidateQueries({
        queryKey: ['workforce', 'roles', tenantDomain, variables.roleId]
      })
      queryClient.invalidateQueries({ queryKey: ['workforce', 'dashboard', tenantDomain] })
    },
    onError: (err) => {
      console.error('[UpdateRole] Error:', err)
      toast.error(err instanceof Error ? err.message : 'Erro ao atualizar cargo')
    }
  })
}

// ============================================================================
// OPTIMISTIC UPDATE MUTATIONS
// ============================================================================

/**
 * useUpdateEmployeeStatus - Optimistic mutation hook for employee status changes
 *
 * Features:
 * - Immediate UI update (optimistic)
 * - Automatic rollback on error
 * - Cache synchronization on success
 * - Sub-100ms perceived response time
 *
 * Usage:
 * ```tsx
 * const updateStatus = useUpdateEmployeeStatus()
 *
 * updateStatus.mutate({
 *   employeeId: 'emp-123',
 *   status: 'INACTIVE',
 *   endDate: '2025-12-31',
 *   reason: 'Contract ended'
 * })
 * ```
 *
 * @returns Mutation object with optimistic updates
 */
export function useUpdateEmployeeStatus() {
  const queryClient = useQueryClient()
  const { tenantDomain } = useTenant()

  return useMutation({
    mutationFn: async ({
      employeeId,
      status,
      endDate,
      reason
    }: {
      employeeId: string
      status: 'ACTIVE' | 'INACTIVE' | 'TERMINATED'
      endDate?: string
      reason?: string
    }) => {
      // Use dedicated status update endpoint
      const { updateEmployeeStatus } = await import('@/api/workforce')
      return await updateEmployeeStatus(tenantDomain, employeeId, status, endDate, reason)
    },

    // Optimistically update cache before API call
    onMutate: async ({ employeeId, status }) => {
      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['workforce', 'employees', tenantDomain] })

      // Snapshot the previous value for rollback
      const previousEmployees = queryClient.getQueriesData({ queryKey: ['workforce', 'employees', tenantDomain] })

      // Optimistically update all employee list queries
      queryClient.setQueriesData<any>({ queryKey: ['workforce', 'employees', tenantDomain] }, (old: any) => {
        if (!old) return old

        // Handle different response structures
        if (Array.isArray(old)) {
          return old.map((emp: Employee) =>
            emp.employeeId === employeeId
              ? { ...emp, employment: { ...emp.employment, status } }
              : emp
          )
        }

        if (old.items && Array.isArray(old.items)) {
          return {
            ...old,
            items: old.items.map((emp: Employee) =>
              emp.employeeId === employeeId
                ? { ...emp, employment: { ...emp.employment, status } }
                : emp
            )
          }
        }

        return old
      })

      // Return context with previous data for rollback
      return { previousEmployees }
    },

    // Rollback on error
    onError: (_err, _variables, context) => {
      if (context?.previousEmployees) {
        // Restore all previous query data
        context.previousEmployees.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
    },

    // Always refetch after success or error to ensure cache consistency
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['workforce', 'employees', tenantDomain] })
      queryClient.invalidateQueries({ queryKey: ['workforce', 'employees', tenantDomain, 'stats'] })
    }
  })
}

/**
 * useReassignManager - Optimistic mutation hook for manager reassignment
 *
 * Features:
 * - Immediate hierarchy update (optimistic)
 * - Automatic rollback on error
 * - Updates employee's manager and related hierarchy data
 * - Sub-100ms perceived response time
 *
 * Usage:
 * ```tsx
 * const reassignManager = useReassignManager()
 *
 * reassignManager.mutate({
 *   employeeId: 'emp-123',
 *   newManagerId: 'emp-456'
 * })
 * ```
 *
 * @returns Mutation object with optimistic updates
 */
export function useReassignManager() {
  const queryClient = useQueryClient()
  const { tenantDomain } = useTenant()

  return useMutation({
    mutationFn: async ({
      employeeId,
      newManagerId,
      newManagerName,
      newManagerEmail
    }: {
      employeeId: string;
      newManagerId: string | null;
      newManagerName?: string | null;
      newManagerEmail?: string | null;
    }) => {
      // Use PATCH endpoint to update managerId at ROOT level (new architecture!)
      const { patchEmployee } = await import('@/api/workforce')
      return await patchEmployee(tenantDomain, employeeId, {
        managerId: newManagerId
      })
    },

    // Optimistically update cache before API call
    onMutate: async ({ employeeId, newManagerId, newManagerName, newManagerEmail }) => {
      // Cancel any outgoing refetches to prevent overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['workforce', 'employees', tenantDomain] })
      await queryClient.cancelQueries({ queryKey: ['workforce', 'hierarchy', tenantDomain] })

      // Snapshot the previous values for rollback
      const previousEmployees = queryClient.getQueriesData({ queryKey: ['workforce', 'employees', tenantDomain] })
      const previousHierarchy = queryClient.getQueryData(['workforce', 'hierarchy', tenantDomain])

      // Optimistically update all employee list queries
      queryClient.setQueriesData<any>({ queryKey: ['workforce', 'employees', tenantDomain] }, (old: any) => {
        if (!old) return old

        // Handle different response structures
        if (Array.isArray(old)) {
          return old.map((emp: Employee) =>
            emp.employeeId === employeeId
              ? {
                  ...emp,
                  managerId: newManagerId,
                  managerName: newManagerName ?? null,
                  managerEmail: newManagerEmail ?? null,
                  currentEngagement: emp.currentEngagement
                    ? { ...emp.currentEngagement, managerId: newManagerId }
                    : null,
                  employment: emp.employment
                    ? { ...emp.employment, managerId: newManagerId }
                    : undefined
                }
              : emp
          )
        }

        if (old.items && Array.isArray(old.items)) {
          return {
            ...old,
            items: old.items.map((emp: Employee) =>
              emp.employeeId === employeeId
                ? {
                    ...emp,
                    managerId: newManagerId,
                    managerName: newManagerName ?? null,
                    managerEmail: newManagerEmail ?? null,
                    currentEngagement: emp.currentEngagement
                      ? { ...emp.currentEngagement, managerId: newManagerId }
                      : null,
                    employment: emp.employment
                      ? { ...emp.employment, managerId: newManagerId }
                      : undefined
                  }
                : emp
            )
          }
        }

        return old
      })

      // Return context with previous data for rollback
      return { previousEmployees, previousHierarchy }
    },

    // Success handler
    onSuccess: () => {
      toast.success('Gestor reatribuído com sucesso!')
    },

    // Rollback on error
    onError: (err, _variables, context) => {
      console.error('[ReassignManager] Error:', err)
      toast.error(err instanceof Error ? err.message : 'Erro ao reatribuir gestor')

      if (context?.previousEmployees) {
        // Restore all previous employee query data
        context.previousEmployees.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data)
        })
      }

      if (context?.previousHierarchy) {
        // Restore hierarchy data
        queryClient.setQueryData(['workforce', 'hierarchy', tenantDomain], context.previousHierarchy)
      }
    },

    // Always refetch after success or error to ensure cache consistency
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['workforce', 'employees', tenantDomain] })
      queryClient.invalidateQueries({ queryKey: ['workforce', 'hierarchy', tenantDomain] })
    }
  })
}

// ============================================================================
// BULK OPERATIONS MUTATIONS
// ============================================================================

/**
 * useBulkUpdateEmployeeStatus - Mutation hook for bulk employee status updates
 *
 * Updates status for multiple employees and handles partial failures.
 *
 * @example
 * ```tsx
 * const bulkUpdateStatus = useBulkUpdateEmployeeStatus()
 *
 * const result = await bulkUpdateStatus.mutateAsync({
 *   employeeIds: ['emp-1', 'emp-2'],
 *   status: 'INACTIVE',
 *   employees: [...] // Full employee objects needed for context
 * })
 * ```
 */
export function useBulkUpdateEmployeeStatus() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      employeeIds: string[]
      status: 'ACTIVE' | 'INACTIVE'
      employees: Employee[]
    }) => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await bulkUpdateEmployeeStatus(
        tenantDomain,
        params.employeeIds,
        params.status,
        params.employees
      )
    },

    onSuccess: (result) => {
      if (!tenantDomain) return

      // Show appropriate toast based on results
      if (result.failed === 0) {
        toast.success(`${result.success} colaboradores atualizados com sucesso!`)
      } else if (result.success > 0) {
        toast.warning(
          `${result.success} atualizados com sucesso, ${result.failed} falharam.`
        )
      } else {
        toast.error('Falha ao atualizar colaboradores.')
      }

      // Log errors for debugging
      if (result.errors.length > 0) {
        console.error('[BulkUpdateStatus] Errors:', result.errors)
      }

      // Invalidate all employee queries
      queryClient.invalidateQueries(invalidateWorkforceQueries.employees(tenantDomain))
      queryClient.invalidateQueries(invalidateWorkforceQueries.dashboard(tenantDomain))
    },

    onError: (err) => {
      console.error('[BulkUpdateStatus] Error:', err)
      toast.error(
        err instanceof Error ? err.message : 'Erro ao atualizar status dos colaboradores'
      )
    },
  })
}

/**
 * useBulkTransferDepartment - Mutation hook for bulk department transfers
 *
 * Transfers multiple employees to a new department.
 *
 * @example
 * ```tsx
 * const bulkTransfer = useBulkTransferDepartment()
 *
 * await bulkTransfer.mutateAsync({
 *   employeeIds: ['emp-1', 'emp-2'],
 *   departmentId: 5,
 *   employees: [...]
 * })
 * ```
 */
export function useBulkTransferDepartment() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      employeeIds: string[]
      departmentId: number
      employees: Employee[]
    }) => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await bulkTransferDepartment(
        tenantDomain,
        params.employeeIds,
        params.departmentId,
        params.employees
      )
    },

    onSuccess: (result) => {
      if (!tenantDomain) return

      if (result.failed === 0) {
        toast.success(`${result.success} colaboradores transferidos com sucesso!`)
      } else if (result.success > 0) {
        toast.warning(
          `${result.success} transferidos, ${result.failed} falharam.`
        )
      } else {
        toast.error('Falha ao transferir colaboradores.')
      }

      if (result.errors.length > 0) {
        console.error('[BulkTransferDepartment] Errors:', result.errors)
      }

      // Invalidate employee and department queries
      queryClient.invalidateQueries(invalidateWorkforceQueries.employees(tenantDomain))
      queryClient.invalidateQueries(invalidateWorkforceQueries.departments(tenantDomain))
      queryClient.invalidateQueries(invalidateWorkforceQueries.dashboard(tenantDomain))
    },

    onError: (err) => {
      console.error('[BulkTransferDepartment] Error:', err)
      toast.error(
        err instanceof Error ? err.message : 'Erro ao transferir colaboradores'
      )
    },
  })
}

/**
 * useBulkReassignManager - Mutation hook for bulk manager reassignments
 *
 * Reassigns multiple employees to a new manager.
 *
 * @example
 * ```tsx
 * const bulkReassign = useBulkReassignManager()
 *
 * await bulkReassign.mutateAsync({
 *   employeeIds: ['emp-1', 'emp-2'],
 *   managerId: 'emp-manager',
 *   employees: [...]
 * })
 * ```
 */
export function useBulkReassignManager() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: {
      employeeIds: string[]
      managerId: string | null
      employees: Employee[]
    }) => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await bulkReassignManager(
        tenantDomain,
        params.employeeIds,
        params.managerId,
        params.employees
      )
    },

    onSuccess: (result) => {
      if (!tenantDomain) return

      if (result.failed === 0) {
        toast.success(`${result.success} colaboradores reatribuídos com sucesso!`)
      } else if (result.success > 0) {
        toast.warning(
          `${result.success} reatribuídos, ${result.failed} falharam.`
        )
      } else {
        toast.error('Falha ao reatribuir colaboradores.')
      }

      if (result.errors.length > 0) {
        console.error('[BulkReassignManager] Errors:', result.errors)
      }

      // Invalidate employee and hierarchy queries
      queryClient.invalidateQueries(invalidateWorkforceQueries.employees(tenantDomain))
      queryClient.invalidateQueries(invalidateWorkforceQueries.hierarchy(tenantDomain))
      queryClient.invalidateQueries(invalidateWorkforceQueries.dashboard(tenantDomain))
    },

    onError: (err) => {
      console.error('[BulkReassignManager] Error:', err)
      toast.error(
        err instanceof Error ? err.message : 'Erro ao reatribuir gestor'
      )
    },
  })
}

// ============================================================================
// PAYROLL MUTATIONS
// ============================================================================

/**
 * useBatchProcessPayroll - Mutation hook for batch payroll processing
 *
 * Processes multiple payroll records in a batch with transaction support.
 *
 * @example
 * ```tsx
 * const batchProcess = useBatchProcessPayroll()
 *
 * const result = await batchProcess.mutateAsync([
 *   { employeeId: 'emp-1', period: '2025-01', grossSalary: 5000, ... },
 *   { employeeId: 'emp-2', period: '2025-01', grossSalary: 6000, ... },
 * ])
 * ```
 */
export function useBatchProcessPayroll() {
  const { tenantDomain } = useTenant()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payrollRecords: CreatePayrollCostData[]) => {
      if (!tenantDomain) throw new Error('Tenant domain required')
      return await batchProcessPayroll(tenantDomain, payrollRecords)
    },

    onSuccess: (result) => {
      if (!tenantDomain) return

      if (result.failed === 0) {
        toast.success(`${result.processed} registros de folha processados com sucesso!`)
      } else if (result.successfulChunks > 0) {
        toast.warning(
          `${result.processed - result.failed} processados, ${result.failed} falharam.`
        )
      } else {
        toast.error('Falha ao processar folha de pagamento.')
      }

      if (result.errors.length > 0) {
        console.error('[BatchProcessPayroll] Errors:', result.errors)
      }

      // Invalidate payroll queries
      queryClient.invalidateQueries(invalidateWorkforceQueries.payroll(tenantDomain))

      // Invalidate dashboard
      queryClient.invalidateQueries(invalidateWorkforceQueries.dashboard(tenantDomain))
    },

    onError: (err) => {
      console.error('[BatchProcessPayroll] Error:', err)
      toast.error(
        err instanceof Error ? err.message : 'Erro ao processar folha de pagamento'
      )
    },
  })
}

// ============================================================================
// EMPLOYEE PICTURE UPLOAD
// ============================================================================

/**
 * useUploadEmployeePicture - Mutation hook for employee picture upload
 *
 * Two-step process:
 * 1. Get presigned URL from backend
 * 2. Upload file directly to S3
 *
 * @example
 * ```tsx
 * const uploadPicture = useUploadEmployeePicture()
 *
 * await uploadPicture.mutateAsync({
 *   employeeId: 'emp-123',
 *   file: pictureFile,
 * })
 * ```
 */
export function useUploadEmployeePicture() {
  const queryClient = useQueryClient()
  const { tenantDomain } = useTenant()

  return useMutation({
    mutationFn: async (params: { employeeId: string; file: File }) => {
      // Step 1: Get presigned URL
      const uploadUrlResponse = await getEmployeePictureUploadUrl(
        params.employeeId,
        {
          mimeType: params.file.type,
          extension: `.${params.file.name.split('.').pop() || 'jpg'}`,
          originalFilename: params.file.name,
          contentLength: params.file.size,
        }
      )

      // Step 2: Upload to S3
      await uploadEmployeePictureToS3(uploadUrlResponse.signedUrl, params.file)

      // Step 3: Confirm upload — updates DDB record with pictureKey
      if (uploadUrlResponse.key) {
        await confirmEmployeePictureUpload(params.employeeId, uploadUrlResponse.key)
      }

      return uploadUrlResponse
    },

    onSuccess: (_, variables) => {
      if (!tenantDomain) return

      toast.success('Foto atualizada com sucesso!')

      // Invalidate employee query to fetch updated picture URL
      queryClient.invalidateQueries(
        invalidateWorkforceQueries.employee(tenantDomain, variables.employeeId)
      )
    },

    onError: (err) => {
      console.error('[UploadEmployeePicture] Error:', err)
      toast.error(
        err instanceof Error ? err.message : 'Erro ao fazer upload da foto'
      )
    },
  })
}
